<script lang="ts">
  /**
   * Dépenser l'Expérience.
   *
   * Le barème du Codex II p.39 se lit sur la valeur **visée** : c'est le point
   * qu'on s'apprête à gagner qui fixe le tarif, pas celui qu'on a déjà. Les
   * Compétences se lisent par famille, comme sur la fiche de papier — quarante
   * et une lignes d'affilée ne se lisent pas, six paquets de sept si.
   */
  import {
    progression as lireLaProgression,
    type Fiche,
    type PersonnageEnregistre,
  } from '../../../rules'

  let {
    vue,
    personnage,
    acheter,
    acheterMaitrise,
    acheterCapacite,
    basculerNarratif,
    fermer,
  }: {
    vue: Fiche
    personnage: PersonnageEnregistre
    /** Achète une Augmentation : la valeur monte, l'XP dépensée suit. */
    acheter: (quoi: 'caracteristique' | 'competence', id: string) => void
    /** Acquiert ou améliore une Maîtrise. */
    acheterMaitrise: (categorie: string) => void
    /** Apprend une Capacité commune, Prérequis vérifiés. */
    acheterCapacite: (id: string) => void
    /** Coche, ou décoche, un Prérequis que la table a tranché. */
    basculerNarratif: (texte: string) => void
    fermer: () => void
  } = $props()

  const marche = $derived(lireLaProgression(personnage))

  const parCarac = $derived(
    vue.caracteristiques.map((carac) => ({
      carac,
      achat: marche.caracteristiques.find((l) => l.id === carac.id),
      competences: marche.competences.filter((l) => l.carac === carac.id),
    })),
  )
</script>

<div class="cartouche">
  <div class="bandeau">
    Dépenser l'Expérience
    <button type="button" class="refermer" onclick={fermer}>Refermer</button>
  </div>
  <div class="corps">
    <p class="regle">
      Une Augmentation vaut +1, et se paie au tarif de la valeur <strong>visée</strong>
      — 29 → 30 coûte 25 XP, 30 → 31 en coûte 35 (Codex II p.39). Il reste
      <strong class="data">{marche.xp_disponible} XP</strong>.
    </p>

    {#each parCarac as famille (famille.carac.id)}
      <section class="famille">
        <h3>
          {famille.carac.nom}
          <span class="valeur data">{famille.carac.valeur}</span>
          {#if famille.achat}
            {#if famille.achat.cout === null}
              <span class="plafond">au plafond</span>
            {:else}
              <button
                type="button"
                class="pilule"
                disabled={!famille.achat.abordable}
                onclick={() => acheter('caracteristique', famille.carac.id)}
              >
                → {famille.achat.actuelle + 1}
                <span class="data">{famille.achat.cout} XP</span>
              </button>
              <span class="rendement">
                et +1 à ses {famille.achat.competences_portees} Compétences
              </span>
            {/if}
          {/if}
        </h3>
        <ul class="achats">
          {#each famille.competences as l (l.id)}
            <li class:hors={l.cout === null}>
              <span class="nom">{l.nom}</span>
              <span class="chiffres data">
                {l.actuelle}<span class="brut">brut</span>
                <span class="fleche">·</span>
                {l.reelle}<span class="brut">réelle</span>
              </span>
              {#if l.cout === null}
                <span class="plafond">au plafond</span>
              {:else}
                <button
                  type="button"
                  class="pilule"
                  disabled={!l.abordable}
                  onclick={() => acheter('competence', l.id)}
                >
                  → {l.reelle_apres}
                  <span class="data">{l.cout} XP</span>
                </button>
              {/if}
            </li>
          {/each}
        </ul>
      </section>
    {/each}

    <section class="famille">
      <h3>
        Capacités
        <span class="rendement">
          {marche.capacites.filter((c) => c.accessible).length} à portée sur
          {marche.capacites.length}
        </span>
      </h3>
      <p class="regle">
        Ce qui est à portée vient en tête. Le reste dit ce qui manque — un seuil
        de Compétence, une Maîtrise, ou un fait de jeu que seul le meneur peut
        valider, et qui se coche ici une fois acquis.
      </p>
      <ul class="achats larges">
        {#each marche.capacites as c (c.id)}
          <li class:hors={!c.accessible}>
            <span class="nom">
              {c.nom}
              <span class="exigence">{c.resume}</span>
              {#each c.manques as m (m)}
                <span class="manque">Il manque : {m}</span>
              {/each}
              {#each c.narratifs as n (n.texte)}
                <label class="narratif">
                  <input
                    type="checkbox"
                    checked={n.valide}
                    onchange={() => basculerNarratif(n.texte)}
                  />
                  <span>{n.texte}</span>
                  <span class="voie">à la table</span>
                </label>
              {/each}
            </span>
            <span class="chiffres data">{c.cout === null ? '—' : c.cout + ' XP'}</span>
            <button
              type="button"
              class="pilule"
              disabled={!c.accessible}
              onclick={() => acheterCapacite(c.id)}
            >
              Apprendre
            </button>
          </li>
        {/each}
      </ul>
    </section>

    <section class="famille">
      <h3>Maîtrises</h3>
      <p class="regle">
        L'XP ne suffit pas : le livre demande aussi de l'entraînement ou des
        Confrontations, et c'est le meneur qui tranche.
      </p>
      <ul class="achats larges">
        {#each marche.maitrises as m (m.categorie)}
          <li class:hors={m.prochain === null}>
            <span class="nom">
              {m.nom}
              {#if m.condition}<span class="exigence">{m.condition}</span>{/if}
            </span>
            <span class="chiffres data">
              {m.niveau === 0 ? 'aucune' : 'Niveau ' + m.niveau}
            </span>
            {#if m.prochain === null}
              <span class="plafond">au sommet</span>
            {:else}
              <button
                type="button"
                class="pilule"
                disabled={!m.abordable}
                onclick={() => acheterMaitrise(m.categorie)}
              >
                → Niveau {m.prochain}
                <span class="data">{m.cout} XP</span>
              </button>
            {/if}
          </li>
        {/each}
      </ul>
    </section>
  </div>
</div>

<style>
  h3 {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.65rem;
    color: var(--gold);
    margin: calc(var(--pas) * 3) 0 var(--pas);
  }
  .famille {
    margin-bottom: calc(var(--pas) * 5);
  }
  .famille:last-child {
    margin-bottom: 0;
  }
  .famille h3 {
    display: flex;
    align-items: baseline;
    gap: calc(var(--pas) * 2);
    flex-wrap: wrap;
    border-bottom: 1px solid var(--line-fort);
    padding-bottom: var(--pas);
  }
  .famille h3 .valeur {
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 1.1rem;
    color: var(--ink);
  }
  .rendement {
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.72rem;
    color: var(--ink-soft);
  }
  /* « brut » et « réelle » se lisent sous les chiffres qu'ils qualifient. */
  .brut {
    font-family: 'Cinzel', serif;
    font-size: 0.55rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-soft);
    margin-left: 3px;
  }
  .fleche {
    margin: 0 var(--pas);
  }
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
</style>
